import { getStoredTenantId, setTenantId } from './auth'
import { readCollaborationLoginQuery } from '@/services/collaboration-login/session'
import { invalidateAuthentication } from '@/services/collaboration-login/auth-state'
import { reactive } from 'vue'

/** 启动时向后端确认模式，不能用默认企业编号代替单租户模式。 */
export const loginTenantScope = reactive({
    enabled: true,
    domainTenantId: undefined as string | undefined,
    currentTenantId: undefined as string | undefined
})
let initialization: Promise<void> | undefined
let selectedTenant: string | undefined
let startupTenant: string | undefined

class LoginTenantRequiredError extends Error {
    readonly code = 'LOGIN_TENANT_REQUIRED'

    constructor() {
        super('请从所属企业入口打开，或在密码登录页选择企业')
    }
}

export function isLoginTenantRequired(error: unknown): boolean {
    return error instanceof LoginTenantRequiredError
}

export function captureLoginTenantEntry(href: string): void {
    startupTenant = readCollaborationLoginQuery(href).get('tenantId') || new URL(href).searchParams.get('tenantId') || undefined
}

export function initializeLoginTenantScope(): Promise<void> {
    if (!initialization) {
        const baseUrl = import.meta.env.VITE_APP_API_BASEURL + import.meta.env.VITE_APP_PROXY_PREFIX
        const entryTenant = selectedTenant || (typeof location.href === 'string'
            ? readCollaborationLoginQuery(location.href).get('tenantId') || new URL(location.href).searchParams.get('tenantId')
            : undefined) || startupTenant
        if (entryTenant && !/^[1-9]\d*$/.test(entryTenant)) {
            return Promise.reject(new Error('企业入口编号不合法，请检查应用首页链接'))
        }
        const storedTenant = getStoredTenantId()
        const previousTenant = /^[1-9]\d*$/.test(storedTenant) ? storedTenant : undefined
        const candidate = entryTenant || previousTenant
        initialization = new Promise<void>((resolve, reject) => {
            // 不使用业务 request，防止请求拦截与初始化互相等待。
            uni.request({
                url: baseUrl + '/system/tenant/login-scope',
                data: { domain: location.host, ...(candidate ? { entryTenantId: candidate } : {}) },
                timeout: 10000,
                header: import.meta.env.VITE_REQUEST_TAG ? { tag: String(import.meta.env.VITE_REQUEST_TAG).trim() } : {},
                success: (response) => {
                    const result = response.data as any
                    if (response.statusCode !== 200 || result?.code !== 0 || typeof result.data?.tenantEnabled !== 'boolean') {
                        reject(new Error(result?.msg || '无法确认企业登录范围，请重试'))
                        return
                    }
                    const domainTenant = result.data.tenantId == null ? undefined : String(result.data.tenantId)
                    if (!candidate && domainTenant && !/^[1-9]\d*$/.test(domainTenant)) {
                        reject(new Error('企业入口信息格式异常，请重试'))
                        return
                    }
                    loginTenantScope.enabled = result.data.tenantEnabled
                    // 入口编号只是请求范围；身份及企业有效性由实际登录请求的底层过滤器校验。
                    const resolvedTenant = loginTenantScope.enabled ? candidate || domainTenant : undefined
                    loginTenantScope.domainTenantId = candidate ? undefined : resolvedTenant
                    loginTenantScope.currentTenantId = resolvedTenant
                    if (resolvedTenant) {
                        if (getStoredTenantId() !== resolvedTenant) {
                            invalidateAuthentication()
                        }
                        setTenantId(resolvedTenant)
                    }
                    resolve()
                },
                fail: () => reject(new Error('企业登录范围加载失败，请检查网络后重试'))
            })
        }).catch((error) => {
            initialization = undefined
            throw error
        })
    }
    return initialization
}

export function requireLoginTenant(): string | undefined {
    if (!loginTenantScope.enabled) return undefined
    if (!loginTenantScope.currentTenantId) throw new LoginTenantRequiredError()
    return loginTenantScope.currentTenantId
}

export async function selectLoginTenant(value: string): Promise<void> {
    if (!/^[1-9]\d*$/.test(value)) throw new Error('企业编号不合法')
    selectedTenant = value
    initialization = undefined
    try {
        await initializeLoginTenantScope()
        requireLoginTenant()
    } catch (error) {
        selectedTenant = undefined
        loginTenantScope.currentTenantId = undefined
        loginTenantScope.domainTenantId = undefined
        invalidateAuthentication()
        throw error
    }
}

/** 仅授权页恢复回调范围，服务端仍依据一次性 state 验证企业。 */
export async function restoreAuthorizationTenant(value: string | undefined): Promise<void> {
    await initializeLoginTenantScope()
    if (!loginTenantScope.enabled || !value) return
    if (!/^[1-9]\d*$/.test(value)) throw new Error('授权企业参数不合法，请重新授权')
    if (loginTenantScope.currentTenantId && value !== loginTenantScope.currentTenantId) {
        throw new Error('授权企业与当前登录范围不一致，请重新授权')
    }
    if (getStoredTenantId() !== value) {
        invalidateAuthentication()
    }
    setTenantId(value)
    loginTenantScope.currentTenantId = value
}
